<template>
  <div class="about-page">
    <!-- Hero Section -->
    <section class="about-hero">
      <div class="hero-content">
        <div class="hero-icon">
          <el-icon><Rocket /></el-icon>
        </div>
        <h1>关于 VideoInsight</h1>
        <p class="hero-desc">
          一个基于 AI 的智能视频创作平台，让每个人都能轻松创作出专业级的视频内容
        </p>
        <div class="hero-actions">
          <el-button type="primary" round class="github-btn">
            <el-icon><Platform /></el-icon> GitHub
          </el-button>
          <el-button round class="contact-btn" plain>
            <el-icon><Message /></el-icon> 联系我们
          </el-button>
        </div>
      </div>
    </section>

    <!-- Project Stats -->
    <section class="stats-section">
      <el-card class="stats-card" shadow="hover">
        <div class="stats-header">
          <el-icon class="trophy-icon"><Trophy /></el-icon>
          <h3>项目成果</h3>
        </div>
        <p class="stats-desc">
          VideoInsight 致力于降低视频创作门槛，让 AI 技术真正服务于内容创作者。我们相信每个人都有创作的潜力，只需要合适的工具来释放它。
        </p>
        <div class="stats-grid">
          <div class="stat-item">
            <div class="stat-label">支持平台</div>
            <div class="stat-value text-blue">5 个</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">AI 模型</div>
            <div class="stat-value text-green">8 种</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">输出格式</div>
            <div class="stat-value text-purple">3 种</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">开发周期</div>
            <div class="stat-value text-orange">6 周</div>
          </div>
        </div>
      </el-card>
    </section>

    <!-- Core Features -->
    <section class="core-features">
      <div class="section-title">
        <el-icon><Star /></el-icon> 核心特性
      </div>
      <div class="features-grid">
        <el-card class="feature-card" shadow="hover">
          <div class="feature-icon icon-blue"><el-icon><Cpu /></el-icon></div>
          <h3>AI 驱动</h3>
          <p>基于 Google Gemini 和多种 AI 模型，实现智能视频内容分析和重构</p>
        </el-card>
        <el-card class="feature-card" shadow="hover">
          <div class="feature-icon icon-cyan"><el-icon><Lightning /></el-icon></div>
          <h3>一键生成</h3>
          <p>输入视频链接，自动生成脚本重构、分镜图生成和视频合成</p>
        </el-card>
        <el-card class="feature-card" shadow="hover">
          <div class="feature-icon icon-blue-dark"><el-icon><Cloudy /></el-icon></div>
          <h3>多平台支持</h3>
          <p>支持 YouTube、抖音、B站等主流视频平台的内容解析</p>
        </el-card>
        <el-card class="feature-card" shadow="hover">
          <div class="feature-icon icon-orange"><el-icon><Cellphone /></el-icon></div>
          <h3>响应式设计</h3>
          <p>完美适配桌面端和移动端，随时随地进行视频创作</p>
        </el-card>
      </div>
    </section>

    <!-- Tech Stack -->
    <section class="tech-stack">
      <div class="section-title">
        <el-icon><Connection /></el-icon> 技术栈
      </div>
      <div class="tech-grid">
        <el-card class="tech-card" shadow="hover">
          <div class="tech-icon icon-blue"><el-icon><Monitor /></el-icon></div>
          <h3>前端技术</h3>
          <div class="tags-group">
            <el-tag>Vue 3</el-tag>
            <el-tag>TypeScript</el-tag>
            <el-tag>Element Plus</el-tag>
            <el-tag>Vite</el-tag>
          </div>
        </el-card>
        <el-card class="tech-card" shadow="hover">
          <div class="tech-icon icon-purple"><el-icon><Coin /></el-icon></div>
          <h3>后端技术</h3>
          <div class="tags-group">
            <el-tag type="success">Python</el-tag>
            <el-tag type="success">FastAPI</el-tag>
            <el-tag type="success">Pydantic</el-tag>
            <el-tag type="success">Uvicorn</el-tag>
          </div>
        </el-card>
        <el-card class="tech-card" shadow="hover">
          <div class="tech-icon icon-red"><el-icon><Aim /></el-icon></div>
          <h3>AI 模型</h3>
          <div class="tags-group">
            <el-tag type="warning">Qwen</el-tag>
            <el-tag type="warning">Gemini</el-tag>
            <el-tag type="warning">DeepSeek</el-tag>
            <el-tag type="warning">OpenAI</el-tag>
          </div>
        </el-card>
        <el-card class="tech-card" shadow="hover">
          <div class="tech-icon icon-cyan"><el-icon><Tools /></el-icon></div>
          <h3>工具链</h3>
          <div class="tags-group">
            <el-tag type="info">FFmpeg</el-tag>
            <el-tag type="info">OpenCV</el-tag>
            <el-tag type="info">yt-dlp</el-tag>
            <el-tag type="info">Docker</el-tag>
          </div>
        </el-card>
      </div>
    </section>

    <!-- Roadmap & Team -->
    <div class="bottom-section">
      <!-- Roadmap -->
      <el-card class="roadmap-card" shadow="hover">
        <div class="card-header">
          <el-icon><Guide /></el-icon> 开发路线图
        </div>
        <el-timeline>
          <el-timeline-item
            v-for="(activity, index) in roadmap"
            :key="index"
            :type="activity.type"
            :color="activity.color"
            :hollow="activity.hollow"
            :timestamp="activity.timestamp"
          >
            <h4>{{ activity.title }}</h4>
            <p>{{ activity.content }}</p>
            <el-tag size="small" :type="activity.statusType" effect="dark" v-if="activity.status">{{ activity.status }}</el-tag>
          </el-timeline-item>
        </el-timeline>
      </el-card>

      <!-- Team -->
      <el-card class="team-card" shadow="hover">
        <div class="card-header">
          <el-icon><UserFilled /></el-icon> 开发团队
        </div>
        <div class="team-grid">
          <div class="team-member">
            <div class="member-avatar bg-blue">
              <el-icon><Monitor /></el-icon>
            </div>
            <h3>AI 开发团队</h3>
            <p>全栈开发 & AI 集成</p>
            <div class="member-links">
              <el-button circle size="small"><el-icon><Platform /></el-icon></el-button>
              <el-button circle size="small"><el-icon><Message /></el-icon></el-button>
            </div>
          </div>
          <div class="team-member">
            <div class="member-avatar bg-green">
              <el-icon><Brush /></el-icon>
            </div>
            <h3>产品设计团队</h3>
            <p>UI/UX 设计 & 产品规划</p>
            <div class="member-links">
              <el-button circle size="small"><el-icon><Platform /></el-icon></el-button>
              <el-button circle size="small"><el-icon><Message /></el-icon></el-button>
            </div>
          </div>
        </div>
        <div class="team-footer">
          <p>我们是一个充满激情的团队，致力于用 AI 技术<br>让视频创作变得更简单、更有趣</p>
          <el-icon class="heart-icon"><HeartFilled /></el-icon>
        </div>
      </el-card>
    </div>

    <!-- Contact Footer -->
    <section class="contact-footer">
      <h3><el-icon><Message /></el-icon> 联系我们</h3>
      <p>有任何问题或建议？我们很乐意听到您的声音！</p>
    </section>
  </div>
</template>

<script setup lang="ts">
// Icons are globally registered in main.ts

const roadmap = [
  {
    title: 'Phase 1 - 核心功能',
    content: '实现视频解析、脚本生成和分镜图生成功能',
    timestamp: '已完成',
    type: 'success',
    status: '已完成',
    statusType: 'success'
  },
  {
    title: 'Phase 2 - 视频合成',
    content: '完成视频合成和预览功能，支持多种输出格式',
    timestamp: '已完成',
    type: 'success',
    status: '已完成',
    statusType: 'success'
  },
  {
    title: 'Phase 3 - 用户界面',
    content: '开发完整的 Web 界面和用户交互体验',
    timestamp: '已完成',
    type: 'success',
    status: '已完成',
    statusType: 'success'
  },
  {
    title: 'Phase 4 - 优化部署',
    content: '性能优化、错误处理和生产环境部署',
    timestamp: '进行中',
    type: 'primary',
    color: '#409eff',
    status: '进行中',
    statusType: 'primary'
  },
  {
    title: 'Phase 5 - 功能扩展',
    content: '支持更多 AI 模型、平台和高级编辑功能',
    timestamp: '计划中',
    hollow: true,
    status: '计划中',
    statusType: 'info'
  }
]
</script>

<style scoped>
.about-page {
  background-color: #f5f7fa;
  min-height: 100%;
  padding-bottom: 40px;
}

/* Hero Section */
.about-hero {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  text-align: center;
  padding: 60px 20px;
  border-radius: 0 0 20px 20px;
  margin-bottom: 40px;
}

.hero-content {
  max-width: 800px;
  margin: 0 auto;
}

.hero-icon {
  font-size: 48px;
  margin-bottom: 20px;
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
}

.about-hero h1 {
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 16px;
}

.hero-desc {
  font-size: 16px;
  opacity: 0.9;
  margin-bottom: 30px;
  line-height: 1.6;
}

.hero-actions {
  display: flex;
  justify-content: center;
  gap: 16px;
}

.github-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.4);
  color: white;
}

.github-btn:hover {
  background: white;
  color: #764ba2;
}

.contact-btn {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.6);
  color: white;
}

.contact-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

/* Stats Section */
.stats-section {
  max-width: 1000px;
  margin: -60px auto 40px;
  padding: 0 20px;
  position: relative;
  z-index: 10;
}

.stats-card {
  border-radius: 12px;
  border: none;
}

.stats-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}

.trophy-icon {
  color: #e6a23c;
  font-size: 24px;
}

.stats-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
}

.stats-desc {
  color: #606266;
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 24px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  text-align: center;
  border-top: 1px solid #ebeef5;
  padding-top: 24px;
}

.stat-label {
  font-size: 13px;
  color: #909399;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
}

.text-blue { color: #409eff; }
.text-green { color: #67c23a; }
.text-purple { color: #a855f7; }
.text-orange { color: #e6a23c; }

/* Core Features */
.core-features, .tech-stack {
  max-width: 1200px;
  margin: 0 auto 60px;
  padding: 0 20px;
}

.section-title {
  text-align: center;
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #303133;
}

.section-title .el-icon {
  color: #e6a23c;
}

.features-grid, .tech-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
}

.feature-card, .tech-card {
  border-radius: 12px;
  border: none;
  text-align: center;
  transition: transform 0.3s;
}

.feature-card:hover, .tech-card:hover {
  transform: translateY(-5px);
}

.feature-icon, .tech-icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 28px;
  color: white;
}

.icon-blue { background: #409eff; }
.icon-cyan { background: #36cfc9; }
.icon-blue-dark { background: #2b4c7e; }
.icon-orange { background: #e6a23c; }
.icon-purple { background: #a855f7; }
.icon-red { background: #f56c6c; }

.feature-card h3, .tech-card h3 {
  font-size: 18px;
  margin: 0 0 12px;
  color: #303133;
}

.feature-card p {
  font-size: 14px;
  color: #606266;
  line-height: 1.6;
  margin: 0;
}

.tags-group {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
}

/* Bottom Section: Roadmap & Team */
.bottom-section {
  max-width: 1200px;
  margin: 0 auto 60px;
  padding: 0 20px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.card-header {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.roadmap-card, .team-card {
  border-radius: 12px;
  border: none;
}

.team-grid {
  display: flex;
  justify-content: space-around;
  margin-bottom: 40px;
  padding-top: 20px;
}

.team-member {
  text-align: center;
}

.member-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  color: white;
  font-size: 32px;
}

.bg-blue { background: #409eff; }
.bg-green { background: #67c23a; }

.team-member h3 {
  font-size: 16px;
  margin: 0 0 4px;
  color: #303133;
}

.team-member p {
  font-size: 12px;
  color: #909399;
  margin: 0 0 16px;
}

.member-links {
  display: flex;
  justify-content: center;
  gap: 8px;
}

.team-footer {
  text-align: center;
  border-top: 1px solid #ebeef5;
  padding-top: 20px;
}

.team-footer p {
  font-size: 13px;
  color: #606266;
  line-height: 1.6;
  margin-bottom: 12px;
}

.heart-icon {
  color: #f56c6c;
  font-size: 20px;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
}

/* Contact Footer */
.contact-footer {
  text-align: center;
  padding: 40px 20px;
  background: #ebf5ff;
  border-radius: 12px;
  max-width: 1000px;
  margin: 0 auto;
}

.contact-footer h3 {
  font-size: 20px;
  color: #303133;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.contact-footer p {
  color: #606266;
  margin: 0;
}

@media (max-width: 768px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .bottom-section {
    grid-template-columns: 1fr;
  }
}
</style>
